import React from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { convertMoneyToNumber } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';
import GoldMicrophone from './images/microphone-gold.svg';
import BlueMicrophone from './images/microphone-blue.svg';
import GrayDiamond from './images/diamond-gray.svg';
import GoldDiamond from './images/diamond-gold.svg';
import TransparentDiamond from './images/diamond-transparent.svg';
import GrayArrow from './images/arrow-gray.svg';

import { AspectRatioWrapper, NamedLink, ResponsiveImage } from '../../components';

import css from './ListingCard.module.css';
import TextLimitedLength from '../TextLimitedLength/TextLimitedLength';

const _MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const priceNumber = convertMoneyToNumber(price);
    const formattedPrice = intl.formatMessage(
      { id: priceNumber === 1 ? 'ListingCard.credit' : 'ListingCard.credits' },
      { currency: priceNumber }
    );
    // const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const _PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      ) : null}
    </div>
  );
};

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className = null,
    rootClassName = null,
    intl: _intl,
    listing,
    renderSizes = null,
    setActiveListing = null,
    _showAuthorInfo = true,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', _price, _publicData } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const _authorName = author.attributes.profile.displayName;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const diamondType = () => {
    let array = [GoldDiamond, GrayDiamond, TransparentDiamond];

    return array[Math.floor(Math.random() * array.length)];
  };

  const microphoneType = () => {
    let array = [GoldMicrophone, BlueMicrophone];

    return array[Math.floor(Math.random() * array.length)];
  };

  const _listingDiamondType = diamondType();
  const _listingMicrophoneType = microphoneType();
  const _listingNumberOfListeners = Math.floor(Math.random() * 100);

  const fullName = currentListing.attributes.title;
  const headline = currentListing.attributes.publicData.headline;
  const bio = currentListing.attributes.publicData.bio;
  const expertise = currentListing.attributes.publicData.expertise;

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <div className={css.imageContainer}>
        {/* <div className={css.topSvg} style={listingDiamondType === GoldDiamond ? { backgroundColor: "#FFE885" } : null}>
          <img src={listingDiamondType} />
        </div> */}

        {/* <div className={css.listeners}>
          {listingNumberOfListeners}
          <img src={listingMicrophoneType} />
        </div> */}

        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
          {...setActivePropsMaybe}
        >
          <LazyImage
            rootClassName={css.rootForImage}
            alt={title}
            image={firstImage}
            variants={variants}
            sizes={renderSizes}
          />
        </AspectRatioWrapper>
      </div>

      <div className={css.slideContent}>
        <p className={css.fullName}>{fullName}</p>
        <p className={css.headline}>{headline}</p>
        <TextLimitedLength rootClassName={css.bio} text={bio} maxLength={100} />

        <div className={css.slideFooter}>
          <div className={css.tagsContainer}>
            {expertise?.map((expertise, tagIndex) => (
              <span className={css.tag} key={tagIndex}>
                {expertise}
              </span>
            ))}
          </div>
          <div className={css.link}>
            <img src={GrayArrow} />
          </div>
        </div>
      </div>

      {/* <div className={css.info}>
        <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />
        <div className={css.mainInfo}>
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
          {showAuthorInfo ? (
            <div className={css.authorInfo}>
              <FormattedMessage id="ListingCard.author" values={{ authorName }} />
            </div>
          ) : null}
        </div>
      </div> */}
    </NamedLink>
  );
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(ListingCardComponent);
